import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useVideoMembershipAccess } from '@/hooks/useVideoMembershipAccess';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Play, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const VideoMembershipSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAccess, loading, requiredMembership } = useVideoMembershipAccess('video_hoops_recaps');
  const [verifying, setVerifying] = useState(true);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && user) {
      verifyMembershipActivation();
    }
  }, [sessionId, user]);

  const verifyMembershipActivation = async () => {
    try {
      // Wait a moment for webhook processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // The useVideoMembershipAccess hook will automatically check membership
      // We just need to wait for it to complete
      setVerifying(false);
      
      if (hasAccess) {
        toast.success('Video membership activated successfully!');
      }
    } catch (error) {
      console.error('Error verifying membership:', error);
      toast.error('Error verifying membership activation');
      setVerifying(false);
    }
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  if (loading || verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span>Activating your video membership...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2 text-green-600">
            <CheckCircle className="h-6 w-6" />
            Payment Successful!
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center space-y-2">
            <h3 className="text-lg font-semibold">Video Hoops Recaps Access Activated</h3>
            <p className="text-muted-foreground">
              Your video membership has been successfully activated. You now have access to exclusive basketball game recaps.
            </p>
          </div>

          {requiredMembership && (
            <div className="space-y-3 p-4 bg-muted rounded-lg">
              <h4 className="font-semibold">Membership Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Plan:</span>
                  <span>{requiredMembership.name}</span>
                </div>
                <div className="flex justify-between">
                  <span>Price:</span>
                  <span>{formatPrice(requiredMembership.price_cents)}/{requiredMembership.billing_type}</span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className="text-green-600 font-medium">Active</span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center">
              Ready to watch your exclusive basketball content?
            </p>
            
            <div className="space-y-2">
              <Button 
                onClick={() => navigate('/videos/hoops_recaps/watch')}
                className="w-full"
                size="lg"
              >
                <Play className="mr-2 h-4 w-4" />
                Watch Videos Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => navigate('/athlete-dashboard')}
                className="w-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VideoMembershipSuccess;